import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { libraryAPI } from '../api';
import { toast } from 'sonner';
import {
  BookmarkPlus, SkipForward, ThumbsDown, ChevronDown, ChevronUp,
  ExternalLink, BookOpen, Sparkles,
} from 'lucide-react';

const CARD_SHADES = [
  'bg-white',
  'bg-slate-50/60',
  'bg-gray-50/50',
  'bg-zinc-50/40',
];

function truncateText(text, maxLen = 180) {
  if (!text || text.length <= maxLen) return { truncated: text || '', needsExpand: false };
  const cut = text.lastIndexOf(' ', maxLen);
  return { truncated: text.slice(0, cut > 0 ? cut : maxLen) + '...', needsExpand: true };
}

const DigestArticleCard = ({ article, index = 0, onAction }) => {
  const navigate = useNavigate();
  const [abstractExpanded, setAbstractExpanded] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [actionTaken, setActionTaken] = useState(null);
  const [saving, setSaving] = useState(false);

  const shade = CARD_SHADES[index % CARD_SHADES.length];

  const abstractInfo = truncateText(article.abstract, 200);
  const summaryInfo = truncateText(article.ai_summary, 200);
  const isSummaryUnavailable = article.ai_summary === 'Abstract or full text not available for AI summary.';

  const handleSave = async () => {
    if (saving || !article.pmid) return;
    setSaving(true);
    try {
      await libraryAPI.saveArticle(article.pmid);
      setActionTaken('saved');
      toast.success('Saved to Library');
      onAction?.(article.pmid, 'save');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await libraryAPI.submitFeedback(article.pmid, 'skip');
      setActionTaken('skipped');
      onAction?.(article.pmid, 'skip');
    } catch {
      // silent
    }
  };

  const handleNotRelevant = async () => {
    try {
      await libraryAPI.submitFeedback(article.pmid, 'not_relevant');
      setActionTaken('not_relevant');
      toast.success('Marked as not relevant');
      onAction?.(article.pmid, 'not_relevant');
    } catch {
      toast.error('Failed to record feedback');
    }
  };

  if (actionTaken) {
    const labels = { saved: 'Saved to Library', skipped: 'Skipped', not_relevant: 'Not Relevant' };
    const colors = { saved: 'text-sky-600 bg-sky-50', skipped: 'text-slate-500 bg-slate-50', not_relevant: 'text-rose-500 bg-rose-50' };
    return (
      <div
        className={`rounded-xl border border-slate-200/60 px-5 py-3 flex items-center justify-between ${shade}`}
        data-testid={`digest-card-dismissed-${article.pmid}`}
      >
        <span className="responsive-title text-sm text-slate-500 truncate max-w-[70%]">{article.title}</span>
        <Badge className={`${colors[actionTaken]} border-0 text-xs`}>{labels[actionTaken]}</Badge>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-slate-200/80 shadow-sm ${shade} transition-shadow hover:shadow-md`}
      data-testid={`digest-card-${article.pmid}`}
    >
      {/* ─── Row 1: Title + metadata ─── */}
      <div className="px-5 pt-4 pb-2">
        <a
          href={article.url || `https://pubmed.ncbi.nlm.nih.gov/${article.pmid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="responsive-title text-base font-semibold text-slate-900 hover:text-sky-700 transition-colors leading-snug line-clamp-2 group"
          data-testid={`digest-card-title-${article.pmid}`}
        >
          {article.title}
          <ExternalLink className="inline-block ml-1 h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity align-text-bottom" />
        </a>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
          {article.journal && <span className="font-medium text-slate-600">{article.journal}</span>}
          {article.pub_date && <span>{article.pub_date}</span>}
          {article.specialty_name && (
            <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-700 px-1.5 py-0">
              {article.specialty_name}
            </Badge>
          )}
          {article.pmid && <span className="text-slate-400">PMID {article.pmid}</span>}
        </div>
      </div>

      {/* ─── Row 2: Abstract ─── */}
      {article.abstract && (
        <div className="px-5 py-2 border-t border-slate-100">
          <button
            onClick={() => setAbstractExpanded(!abstractExpanded)}
            className="flex items-center gap-1.5 mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors"
            data-testid={`toggle-abstract-${article.pmid}`}
          >
            <BookOpen className="h-3 w-3" /> Abstract
            {abstractInfo.needsExpand && (
              abstractExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {abstractExpanded ? article.abstract : abstractInfo.truncated}
          </p>
        </div>
      )}

      {/* ─── Row 3: AI Summary ─── */}
      {article.ai_summary && !isSummaryUnavailable && (
        <div className="px-5 py-2 border-t border-slate-100 bg-sky-50/30">
          <button
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            className="flex items-center gap-1.5 mb-1 text-xs font-semibold uppercase tracking-wider text-sky-500 hover:text-sky-700 transition-colors"
            data-testid={`toggle-summary-${article.pmid}`}
          >
            <Sparkles className="h-3 w-3" /> AI Summary
            {summaryInfo.needsExpand && (
              summaryExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
          <p className="text-sm text-slate-700 leading-relaxed">
            {summaryExpanded ? article.ai_summary : summaryInfo.truncated}
          </p>
        </div>
      )}

      {/* ─── Row 4: Action buttons ─── */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="bg-sky-600 hover:bg-sky-700 text-white text-xs"
          data-testid={`save-btn-${article.pmid}`}
        >
          <BookmarkPlus className="h-3.5 w-3.5 mr-1" />
          {saving ? 'Saving...' : 'Save to Library'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSkip}
          className="text-xs text-slate-500 border-slate-200 hover:bg-slate-50"
          data-testid={`skip-btn-${article.pmid}`}
        >
          <SkipForward className="h-3.5 w-3.5 mr-1" />
          Skip
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNotRelevant}
          className="text-xs text-rose-500 border-rose-200 hover:bg-rose-50"
          data-testid={`not-relevant-btn-${article.pmid}`}
        >
          <ThumbsDown className="h-3.5 w-3.5 mr-1" />
          Not Relevant
        </Button>
      </div>
    </div>
  );
};

export default DigestArticleCard;
